import React, { Fragment } from 'react';

import type { ExtensionParams } from '@atlaskit/editor-common/extensions/extension-handler';
import type { EditorContentMode } from '@atlaskit/editor-common/types/editor-appearance';
import { RendererActionsContext } from '@atlaskit/renderer/actions/renderer-actions-context';

import type { ADFRendererProps } from '@confluence/adf-renderer/entry-points/ADFRendererComponent.ts';
import { EXTENSION_KEY } from '@confluence/fabric-extension-lib/entry-points/extensionConstants.ts';
import { createMacroExtensionHandler } from '@confluence/fabric-extension-lib/entry-points/createMacroExtensionHandler.ts';
import { fg } from '@confluence/feature-gating';
import {
	RENDERER,
	getExperienceName,
	getMacroAttributesFromADFNode,
} from '@confluence/macro-tracker/entry-points/MacroExperienceTracker.ts';
import { MacroExperienceStart } from '@confluence/macro-tracker/entry-points/MacroExperienceStart.ts';
import { ExperienceTimeout } from '@confluence/experience-tracker/entry-points/ExperienceTimeout.ts';
import { InlineExternalImage } from '@confluence/inline-external-image/entry-points/InlineExternalImage.ts';
import type { InlineExternalImageParams } from '@confluence/inline-external-image/entry-points/InlineExternalImage.ts';
import { LoadablePlaceholderServer } from '@confluence/loadable/entry-points/LoadablePlaceholder.ts';

import { LegacyContentExtensionHandlerPaint } from '../LegacyContentExtension/index.ts';

import { isInitialLcmSsrEligible, isLegacyContentExtension } from './lcmSsrEligibility.ts';
import { PlaceholderAwareInlineNodeRendererWrapper } from './PlaceholderAwareInlineNodeRendererWrapper.tsx';

export const getSsrMigrationExtHandlerForRenderer = ({
	contentId,
	contentMode,
	clientAnnotationProvider,
	clientHydrationReady,
	getLcmClientExtensionHandlers,
	getLcmNestedExtensionHandlers,
	nestedLcmSsrEnabled,
}: {
	contentId: any;
	contentMode?: EditorContentMode;
	clientAnnotationProvider?: ADFRendererProps['annotationProvider'];
	clientHydrationReady?: boolean;
	getLcmClientExtensionHandlers?: () => NonNullable<ADFRendererProps['extensionHandlers']>;
	getLcmNestedExtensionHandlers?: () => NonNullable<ADFRendererProps['extensionHandlers']>;
	/**
	 * Leave unset to have the gate resolved per node, which keeps the exposure confined to pages that
	 * actually contain an LCM. Callers that have already established the decision pass it explicitly.
	 */
	nestedLcmSsrEnabled?: boolean;
}): ((extension: ExtensionParams<any>, ...args: any[]) => React.ReactElement) =>
	createMacroExtensionHandler((extension) => {
		if (!extension) {
			return null;
		}
		const { type, extensionKey, parameters } = extension;

		const attributes = getMacroAttributesFromADFNode(extension);
		const macroId = attributes?.macroId;
		const defaultProps = {
			contentId,
			mode: RENDERER,
			extensionKey,
			attributes,
			node: extension,
		};

		let content: React.ReactNode | null = null;
		if (extensionKey === EXTENSION_KEY.INLINE_EXTERNAL_IMAGE) {
			const inlineExternalImageProps = parameters as InlineExternalImageParams;
			content = (
				<Fragment>
					<MacroExperienceStart
						name={getExperienceName(RENDERER, extension)}
						mode={RENDERER}
						extensionKey={extensionKey}
						attributes={attributes}
						timeout={ExperienceTimeout.MACRO_LOAD}
						contentId={contentId}
					/>
					<RendererActionsContext>
						<InlineExternalImage {...inlineExternalImageProps} {...defaultProps} />
					</RendererActionsContext>
				</Fragment>
			);
		}

		// Resolved only for LCM nodes so that pages without one are never exposed to the nested gate.
		const nestedSsrEnabled = isLegacyContentExtension(extension)
			? (nestedLcmSsrEnabled ?? fg('confluence_lcm_ssr_nested'))
			: false;

		// When nested SSR is enabled every LCM qualifies; otherwise only content without nested
		// extensions is eligible.
		if (
			isLegacyContentExtension(extension) &&
			(nestedSsrEnabled || isInitialLcmSsrEligible(extension))
		) {
			content = (
				<LegacyContentExtensionHandlerPaint
					clientAnnotationProvider={clientHydrationReady ? clientAnnotationProvider : undefined}
					clientExtensionHandlers={
						clientHydrationReady ? getLcmClientExtensionHandlers?.() : undefined
					}
					clientHydrationReady={clientHydrationReady}
					contentId={contentId}
					contentMode={contentMode}
					enableHydrationBridge={nestedSsrEnabled}
					extensionHandlers={nestedSsrEnabled ? getLcmNestedExtensionHandlers?.() : undefined}
					node={extension}
				/>
			);
		}

		// This boundary is not directly used but it provides reference for fixing nested div under span.
		// See next/packages/ssr-scripts-utils/src/fixUnbalancedPlaceholderCommentMarkers.ts for details
		return (
			<LoadablePlaceholderServer placeholderId={`migration-ext-container-${macroId}`}>
				{type === 'inlineExtension' ? (
					// Mimic the logic in platform/packages/editor/renderer/src/ui/ExtensionRenderer.tsx
					// We don't update the platform code for 2 reasons:
					// 1. Backwards compatibility. I don't know if other product supplies this wrapper already (unlikely)
					// 2. More importantly this needs to be inside the migration-ext-container-${macroId} boundary.
					//    Inline macros has <span> nested in <div> problem. We are using the boundary as guide to fix them.
					//    See next/packages/ssr-scripts-utils/src/fixUnbalancedPlaceholderCommentMarkers.ts
					// eslint-disable-next-line @atlaskit/design-system/no-unsafe-style-overrides
					<PlaceholderAwareInlineNodeRendererWrapper macroId={macroId}>
						{content}
					</PlaceholderAwareInlineNodeRendererWrapper>
				) : (
					content
				)}
			</LoadablePlaceholderServer>
		);
	});
